import type { StructuralRange, StructuralRangeKind } from '../types/structuralRangeTypes'
import { STRUCTURAL_RANGE_KIND_LABELS } from './structuralRangeTaxonomy'

/** Chapter-wide display ordinals, one sequence per classification, never
 * persisted in the editorial title. Use the complete row order, not a
 * witness-filtered or virtualized projection.
 */
export function buildStructuralRangeDisplayNumbers(
  ranges: readonly StructuralRange[],
  rowOrderById: ReadonlyMap<string, number>,
): ReadonlyMap<string, number> {
  const counts = new Map<StructuralRangeKind, number>()
  const numbers = new Map<string, number>()
  const ordered = ranges.filter(range => range.status !== 'rejected'
    && range.kind !== null
    && rowOrderById.has(range.startRowId) && rowOrderById.has(range.endRowId))
    .sort((a, b) => (rowOrderById.get(a.startRowId)! - rowOrderById.get(b.startRowId)!)
      // Outer range first at a shared start; UUID breaks exact ties without
      // changing the sequence when an editor merely changes a title.
      || (rowOrderById.get(b.endRowId)! - rowOrderById.get(a.endRowId)!)
      || a.id.localeCompare(b.id))
  for (const range of ordered) {
    const kind = range.kind!
    const number = (counts.get(kind) ?? 0) + 1
    counts.set(kind, number)
    numbers.set(range.id, number)
  }
  return numbers
}

export function formatStructuralRangeKindLabel(
  kind: StructuralRangeKind | null | undefined,
  number?: number,
  fallback = 'Unclassified',
): string {
  const label = kind ? STRUCTURAL_RANGE_KIND_LABELS[kind] : fallback
  return kind && number !== undefined ? `${label} ${number}` : label
}
